<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { getReceipt, getReceiptFilterOptions, getReceipts } from '../api'
import NaftaPosInstanceSelector from '../components/NaftaPosInstanceSelector.vue'
import { chooseNaftaPosSource, session } from '../session'
import { activeNaftaPosInstance, sourceSelectionState } from '../platform/context'
import LocalizedDateTimeField from '../components/receipts/LocalizedDateTimeField.vue'
import ReceiptDrawer from '../components/receipts/ReceiptDrawer.vue'
import { formatReceiptDateTime, parseReceiptDateTime, validateReceiptRange } from '../components/receipts/receiptDateTime.js'
import { useI18n } from '../i18n'

const route = useRoute()
const router = useRouter()
const { locale, t } = useI18n()

const activeStation = computed(() => activeNaftaPosInstance(session.platformContext))
const stationId = computed(() => activeStation.value?.enabled ? String(activeStation.value.id) : '')
const switching = ref(false)
const page = ref(emptyPage())
const currentPage = ref(1)
const pageSize = ref(25)
const search = ref('')
const from = ref('')
const to = ref('')
const types = ref([])
const paymentFormIds = ref([])
const searchDraft = ref('')
const fromDraft = ref({ date: '', time: '' })
const toDraft = ref({ date: '', time: '' })
const typeDraft = ref([])
const paymentDraft = ref([])
const filterOptions = ref({ paymentForms: [] })
const filtersOpen = ref(false)
const loading = ref(true)
const optionsLoading = ref(false)
const error = ref('')
const filterError = ref('')
const searchError = ref('')
const selectedId = ref(null)
const detail = ref(null)
const detailLoading = ref(false)
const detailError = ref('')
let routeReady = false
let initializationRequest = 0
let skipNextRouteLoad = false
let optionsStation = ''
let pageRequest = 0
let optionsRequest = 0
let detailRequest = 0
let disposed = false
const receiptScope = () => JSON.stringify([session.user?.id ?? session.user?.username,
  session.user?.accessVersion, session.platformContext?.activeStationId,
  activeStation.value?.id, activeStation.value?.enabled])
const currentScope = (scope) => !disposed && scope === receiptScope()

const hasConditions = computed(() => Boolean(search.value || from.value || to.value || types.value.length || paymentFormIds.value.length))
const activeFilterGroups = computed(() => Number(Boolean(from.value || to.value)) + Number(Boolean(types.value.length)) + Number(Boolean(paymentFormIds.value.length)))
const resultFrom = computed(() => page.value.totalItems ? ((page.value.page - 1) * page.value.limit) + 1 : 0)
const resultTo = computed(() => page.value.totalItems ? resultFrom.value + page.value.items.length - 1 : 0)
const pageButtons = computed(() => paginationItems(page.value.page, page.value.totalPages))
const chips = computed(() => {
  const result = []
  if (search.value) result.push({ key: 'search', label: `${t('receiptSearch')}: ${search.value}` })
  if (from.value) result.push({ key: 'from', label: t('filterFromChip', { value: displayDate(from.value) }) })
  if (to.value) result.push({ key: 'to', label: t('filterToChip', { value: displayDate(to.value) }) })
  types.value.forEach((value) => result.push({ key: `type:${value}`, label: typeLabel(value) }))
  paymentFormIds.value.forEach((value) => {
    const option = filterOptions.value.paymentForms.find((item) => String(item.paymentFormId) === String(value))
    result.push({ key: `payment:${value}`, label: option?.paymentFormName ?? `#${value}` })
  })
  return result
})

function emptyPage() {
  return {
    items: [], page: 1, limit: 25, totalItems: 0, totalPages: 0,
    hasPrevious: false, hasNext: false, hasMore: false, nextCursor: null,
    sampledAt: null,
    freshness: { status: 'NEVER_SYNCED' },
  }
}

async function loadStations() {
  if (disposed) return
  const request = ++initializationRequest
  let scope = receiptScope()
  const isCurrent = () => request === initializationRequest && currentScope(scope)
  loading.value = true
  error.value = ''
  try {
    const requested = scalar(route.query.station)
    if (requested && requested !== stationId.value && availableSource(requested)) {
      const context = await chooseNaftaPosSource(Number(requested))
      if (!context || request !== initializationRequest || disposed) return
      scope = receiptScope()
    }
    await normalizeRoute(Boolean(requested && requested !== stationId.value))
    if (!isCurrent()) return
    restoreRouteState()
    routeReady = true
    await Promise.all([loadFilterOptions(), loadPage()])
  } catch (cause) {
    if (isCurrent()) error.value = cause.message
  } finally {
    if (isCurrent()) loading.value = false
  }
}

function restoreRouteState() {
  currentPage.value = positiveInteger(route.query.page, 1)
  pageSize.value = [25, 50, 100].includes(Number(scalar(route.query.limit))) ? Number(scalar(route.query.limit)) : 25
  search.value = /^\d{1,32}$/.test(scalar(route.query.search)) ? scalar(route.query.search) : ''
  from.value = validIso(scalar(route.query.from))
  to.value = validIso(scalar(route.query.to))
  types.value = queryValues(route.query.type).filter((value) => ['SALE', 'RETURN', 'ANOMALY'].includes(value))
  paymentFormIds.value = queryValues(route.query.payment).filter((value) => /^-?\d+$/.test(value)).slice(0, 50)
  syncDrafts()
}

async function restoreAndLoad() {
  const scope = receiptScope()
  closeDrawer()
  const requested = scalar(route.query.station)
  if (requested && requested !== stationId.value && availableSource(requested)) {
    await selectStation(Number(requested))
    return
  }
  await normalizeRoute(Boolean(requested && requested !== stationId.value))
  if (!currentScope(scope)) return
  restoreRouteState()
  await Promise.all([optionsStation !== stationId.value ? loadFilterOptions() : null, loadPage()])
}

function availableSource(id) {
  return sourceSelectionState(session.platformContext).sources.some((item) => item.enabled && String(item.id) === String(id))
}

async function normalizeRoute(clearSourceFilters = false) {
  const query = { ...route.query }
  if (stationId.value) query.station = stationId.value
  else delete query.station
  if (clearSourceFilters) { delete query.payment; delete query.page }
  if (router.resolve({ name: 'receipts', query }).fullPath === route.fullPath) return
  skipNextRouteLoad = routeReady
  await router.replace({ name: 'receipts', query })
}

function resetSourceData() {
  pageRequest++; optionsRequest++; detailRequest++
  page.value = emptyPage()
  filterOptions.value = { paymentForms: [] }
  optionsStation = ''
  paymentFormIds.value = []; paymentDraft.value = []
  currentPage.value = 1
  loading.value = false; optionsLoading.value = false
  error.value = ''; filterError.value = ''; searchError.value = ''
  closeDrawer()
}

async function reloadContext() {
  const scope = receiptScope()
  await normalizeRoute(true)
  if (!currentScope(scope)) return
  restoreRouteState()
  await Promise.all([loadFilterOptions(), loadPage()])
}

async function loadFilterOptions() {
  if (!stationId.value) return
  const request = ++optionsRequest, scope = receiptScope(), sourceId = stationId.value
  const isCurrent = () => request === optionsRequest && currentScope(scope)
  optionsLoading.value = true
  try {
    const result = await getReceiptFilterOptions(sourceId)
    if (!isCurrent()) return
    filterOptions.value = result
    optionsStation = sourceId
  } catch (cause) {
    if (!isCurrent()) return
    filterOptions.value = { paymentForms: [] }
    filterError.value = cause.message
  } finally {
    if (isCurrent()) optionsLoading.value = false
  }
}

async function loadPage() {
  const request = ++pageRequest, scope = receiptScope(), sourceId = stationId.value
  const isCurrent = () => request === pageRequest && currentScope(scope)
  if (!sourceId) { loading.value = false; return }
  loading.value = true
  error.value = ''
  try {
    const result = await getReceipts(sourceId, {
      page: currentPage.value,
      limit: pageSize.value,
      search: search.value,
      from: from.value,
      to: to.value,
      types: types.value,
      paymentFormIds: paymentFormIds.value,
    })
    if (!isCurrent()) return
    page.value = result
    if (page.value.page !== currentPage.value) {
      currentPage.value = page.value.page
      skipNextRouteLoad = true
      await router.replace({ name: 'receipts', query: buildQuery() })
    }
  } catch (cause) {
    if (!isCurrent()) return
    page.value = emptyPage()
    error.value = cause.message
  } finally {
    if (isCurrent()) loading.value = false
  }
}

async function selectStation(sourceId) {
  if (!sourceId || String(sourceId) === stationId.value || switching.value || !availableSource(sourceId)) return
  const scope = receiptScope()
  switching.value = true
  resetSourceData()
  error.value = ''
  try {
    await chooseNaftaPosSource(sourceId)
  } catch (cause) {
    if (currentScope(scope)) error.value = cause.message
  } finally { switching.value = false }
}

async function applySearch() {
  const value = searchDraft.value.trim()
  searchError.value = ''
  if (value && !/^\d{1,32}$/.test(value)) {
    searchError.value = t('receiptSearchInvalid')
    return
  }
  await navigate({ search: value, page: 1 })
}

async function applyFilters() {
  filterError.value = ''
  let nextFrom = ''
  let nextTo = ''
  try {
    nextFrom = parseReceiptDateTime(fromDraft.value.date, fromDraft.value.time)
    nextTo = parseReceiptDateTime(toDraft.value.date, toDraft.value.time)
  } catch {
    filterError.value = t('invalidReceiptDateTime')
    return
  }
  if (!validateReceiptRange(nextFrom, nextTo)) {
    filterError.value = t('invalidReceiptPeriod')
    return
  }
  filtersOpen.value = false
  await navigate({
    from: nextFrom,
    to: nextTo,
    types: [...typeDraft.value],
    paymentFormIds: [...paymentDraft.value],
    page: 1,
  })
}

async function removeChip(key) {
  if (key === 'search') return navigate({ search: '', page: 1 })
  if (key === 'from') return navigate({ from: '', page: 1 })
  if (key === 'to') return navigate({ to: '', page: 1 })
  if (key.startsWith('type:')) return navigate({ types: types.value.filter((item) => item !== key.slice(5)), page: 1 })
  if (key.startsWith('payment:')) return navigate({ paymentFormIds: paymentFormIds.value.filter((item) => String(item) !== key.slice(8)), page: 1 })
}

async function clearFilters() {
  filtersOpen.value = false
  await navigate({ search: '', from: '', to: '', types: [], paymentFormIds: [], page: 1 })
}

async function navigate(patch = {}) {
  const state = {
    station: patch.station ?? stationId.value,
    page: patch.page ?? currentPage.value,
    limit: patch.limit ?? pageSize.value,
    search: patch.search ?? search.value,
    from: patch.from ?? from.value,
    to: patch.to ?? to.value,
    types: patch.types ?? types.value,
    paymentFormIds: patch.paymentFormIds ?? paymentFormIds.value,
  }
  await router.push({ name: 'receipts', query: queryForState(state) })
}

function buildQuery() {
  return queryForState({
    station: stationId.value, page: currentPage.value, limit: pageSize.value,
    search: search.value, from: from.value, to: to.value,
    types: types.value, paymentFormIds: paymentFormIds.value,
  })
}

function queryForState(state) {
  const query = { station: state.station }
  if (state.page > 1) query.page = String(state.page)
  if (state.limit !== 25) query.limit = String(state.limit)
  if (state.search) query.search = state.search
  if (state.from) query.from = state.from
  if (state.to) query.to = state.to
  if (state.types.length) query.type = state.types
  if (state.paymentFormIds.length) query.payment = state.paymentFormIds.map(String)
  return query
}

function syncDrafts() {
  searchDraft.value = search.value
  fromDraft.value = formatReceiptDateTime(from.value)
  toDraft.value = formatReceiptDateTime(to.value)
  typeDraft.value = [...types.value]
  paymentDraft.value = paymentFormIds.value.map(String)
  filterError.value = ''
  searchError.value = ''
}

async function openReceipt(item) {
  const request = ++detailRequest, scope = receiptScope(), sourceId = stationId.value
  if (!sourceId) return
  const isCurrent = () => request === detailRequest && currentScope(scope) && selectedId.value === item.id
  selectedId.value = item.id
  detail.value = null
  detailError.value = ''
  detailLoading.value = true
  try {
    const result = await getReceipt(sourceId, item.id)
    if (isCurrent()) detail.value = result.receipt
  } catch (cause) {
    if (isCurrent()) detailError.value = cause.message
  } finally {
    if (isCurrent()) detailLoading.value = false
  }
}

function closeDrawer() {
  detailRequest++
  selectedId.value = null
  detail.value = null
  detailError.value = ''
  detailLoading.value = false
}

function scalar(value) { return Array.isArray(value) ? String(value[0] ?? '') : String(value ?? '') }
function queryValues(value) { return (Array.isArray(value) ? value : value == null ? [] : [value]).map(String) }
function positiveInteger(value, fallback) { const number = Number(scalar(value)); return Number.isInteger(number) && number > 0 ? number : fallback }
function validIso(value) { if (!value) return ''; const parsed = new Date(value); return Number.isNaN(parsed.getTime()) ? '' : parsed.toISOString() }
function localeCode() { return ({ uk: 'uk-UA', ru: 'ru-RU', en: 'en-GB' })[locale.value] ?? 'uk-UA' }
function displayDate(value) { return value ? new Date(value).toLocaleString(localeCode(), { dateStyle: 'short', timeStyle: 'short' }) : '—' }
function displaySampledAt(value) { return value ? new Date(value).toLocaleString(localeCode(), { dateStyle: 'short', timeStyle: 'medium' }) : '—' }
function dateTime(item) { return item.createdAt ? displayDate(item.createdAt) : item.createdAtRaw || '—' }
function typeLabel(type) { return type === 'RETURN' ? t('receiptReturn') : type === 'ANOMALY' ? t('receiptAnomaly') : t('receiptSale') }
function paginationItems(current, total) {
  if (total <= 1) return total ? [1] : []
  if (total <= 7) return Array.from({ length: total }, (_, index) => index + 1)
  const values = new Set([1, total, current - 1, current, current + 1])
  const sorted = [...values].filter((value) => value >= 1 && value <= total).sort((a, b) => a - b)
  const result = []
  sorted.forEach((value, index) => {
    if (index && value - sorted[index - 1] > 1) result.push(`ellipsis-${value}`)
    result.push(value)
  })
  return result
}

watch(() => route.fullPath, async () => {
  if (!routeReady) return
  if (skipNextRouteLoad) { skipNextRouteLoad = false; return }
  await restoreAndLoad()
})

onMounted(loadStations)
watch(receiptScope, () => {
  resetSourceData()
  if (routeReady) void reloadContext()
  else void loadStations()
}, { flush: 'sync' })
onBeforeUnmount(() => { disposed = true; initializationRequest++; pageRequest++; optionsRequest++; detailRequest++ })
</script>

<template>
  <section class="receipts-page">
    <header class="receipts-title-row">
      <div>
        <p class="eyebrow">{{ t('work') }}</p>
        <h1>{{ t('receiptsTitle') }}</h1>
        <p class="muted">{{ t('receiptsHint') }}</p>
      </div>
      <div class="receipts-title-actions">
        <NaftaPosInstanceSelector :context="session.platformContext" :disabled="loading || switching" @select="selectStation" />
        <button class="button receipt-refresh" type="button" :disabled="loading || !stationId" @click="loadPage">
          {{ loading ? t('refreshing') : t('refresh') }}
        </button>
      </div>
    </header>

    <div class="receipts-context-row">
      <p v-if="page.sampledAt" class="receipt-freshness">{{ t('receiptUpdatedAt', { value: displaySampledAt(page.sampledAt) }) }}</p>
    </div>

    <p v-if="error" class="notice error" role="alert">{{ error }}</p>

    <section class="card receipts-panel">
      <div class="receipt-search-toolbar">
        <form class="receipt-search" role="search" @submit.prevent="applySearch">
          <label for="receipt-search">{{ t('receiptSearch') }}</label>
          <div class="receipt-search-control">
            <span aria-hidden="true">⌕</span>
            <input id="receipt-search" v-model="searchDraft" inputmode="numeric" :placeholder="t('receiptSearchPlaceholder')" autocomplete="off">
            <button class="button" type="submit" :disabled="loading">{{ t('searchAction') }}</button>
          </div>
          <small v-if="searchError" class="field-error" role="alert">{{ searchError }}</small>
        </form>
        <button class="button button-secondary receipt-filter-toggle" type="button" :aria-expanded="filtersOpen" aria-controls="receipt-filter-panel" @click="filtersOpen = !filtersOpen">
          <span aria-hidden="true">☷</span>
          {{ t('receiptFilters') }}
          <span v-if="activeFilterGroups" class="filter-count">{{ activeFilterGroups }}</span>
        </button>
      </div>

      <form v-if="filtersOpen" id="receipt-filter-panel" class="receipt-filter-panel" @submit.prevent="applyFilters" @keydown.esc="filtersOpen = false">
        <header>
          <h2>{{ t('filterPanelTitle') }}</h2>
          <button class="drawer-close compact" type="button" :aria-label="t('close')" @click="filtersOpen = false">×</button>
        </header>
        <div class="receipt-filter-grid">
          <LocalizedDateTimeField id="receipt-from" v-model="fromDraft" :label="t('periodFrom')" :date-picker-label="t('openDatePicker')" :time-picker-label="t('openTimePicker')" :disabled="loading" :invalid="Boolean(filterError)" />
          <LocalizedDateTimeField id="receipt-to" v-model="toDraft" :label="t('periodTo')" :date-picker-label="t('openDatePicker')" :time-picker-label="t('openTimePicker')" :disabled="loading" :invalid="Boolean(filterError)" />
          <fieldset>
            <legend>{{ t('receiptTypes') }}</legend>
            <label class="checkbox"><input v-model="typeDraft" type="checkbox" value="SALE"> {{ t('receiptSale') }}</label>
            <label class="checkbox"><input v-model="typeDraft" type="checkbox" value="RETURN"> {{ t('receiptReturn') }}</label>
            <label class="checkbox"><input v-model="typeDraft" type="checkbox" value="ANOMALY"> {{ t('receiptAnomaly') }}</label>
          </fieldset>
          <fieldset>
            <legend>{{ t('paymentFormsFilter') }}</legend>
            <p v-if="optionsLoading" class="muted">{{ t('loading') }}</p>
            <label v-for="option in filterOptions.paymentForms" v-else :key="option.paymentFormId" class="checkbox">
              <input v-model="paymentDraft" type="checkbox" :value="String(option.paymentFormId)">
              <span>{{ option.paymentFormName }}</span><small>{{ option.receiptCount }}</small>
            </label>
          </fieldset>
        </div>
        <p v-if="filterError" class="field-error" role="alert">{{ filterError }}</p>
        <footer>
          <button class="button button-secondary" type="button" @click="clearFilters">{{ t('clearAllFilters') }}</button>
          <button class="button" type="submit" :disabled="loading">{{ t('applyFilters') }}</button>
        </footer>
      </form>

      <div v-if="chips.length" class="receipt-filter-chips" aria-live="polite">
        <button v-for="chip in chips" :key="chip.key" type="button" :aria-label="t('removeFilter', { name: chip.label })" @click="removeChip(chip.key)">
          {{ chip.label }} <span aria-hidden="true">×</span>
        </button>
        <button class="clear-all" type="button" @click="clearFilters">{{ t('clearAllFilters') }}</button>
      </div>

      <div class="receipt-table-summary">
        <strong>{{ t('receiptResults', { count: page.totalItems }) }}</strong>
        <span v-if="page.totalItems">{{ t('showing', { from: resultFrom, to: resultTo, total: page.totalItems }) }}</span>
      </div>

      <p v-if="loading" class="drawer-state">{{ t('loading') }}</p>
      <div v-else-if="page.items.length" class="receipts-table-wrap">
        <table class="receipts-table">
          <thead><tr><th>{{ t('receiptId') }}</th><th>{{ t('receiptShiftNumber') }}</th><th>{{ t('receiptNumber') }}</th><th>{{ t('paymentForm') }}</th><th>{{ t('receiptType') }}</th><th>{{ t('receiptItems') }}</th><th>{{ t('receiptDate') }}</th><th :aria-label="t('receiptDetails')"></th></tr></thead>
          <tbody>
            <tr v-for="item in page.items" :key="item.id" :class="{ selected: selectedId === item.id }" tabindex="0" role="button" :aria-label="t('viewReceipt', { id: item.id })" @click="openReceipt(item)" @keydown.enter="openReceipt(item)" @keydown.space.prevent="openReceipt(item)">
              <td><strong>{{ item.id }}</strong></td>
              <td>{{ item.sessionNumber ?? '—' }}</td>
              <td>{{ item.receiptNumber ?? '—' }}</td>
              <td><span v-for="payment in item.paymentForms" :key="payment" class="receipt-payment-tag">{{ payment }}</span><span v-if="!item.paymentForms.length">—</span></td>
              <td><span class="receipt-type" :class="`receipt-type-${item.type.toLowerCase()}`">{{ typeLabel(item.type) }}</span></td>
              <td>{{ item.itemCount }}</td>
              <td>{{ dateTime(item) }}</td>
              <td class="receipt-row-chevron" aria-hidden="true">›</td>
            </tr>
          </tbody>
        </table>
      </div>
      <div v-else class="receipts-empty">
        <strong>{{ hasConditions ? t('filteredReceiptsEmpty') : t('noReceipts') }}</strong>
        <span v-if="hasConditions">{{ t('filteredReceiptsEmptyHint') }}</span>
        <button v-if="hasConditions" class="button button-secondary" type="button" @click="clearFilters">{{ t('clearAllFilters') }}</button>
      </div>

      <footer v-if="page.totalItems" class="receipt-pagination">
        <label>{{ t('rowsPerPage') }}
          <select :value="pageSize" :disabled="loading" @change="navigate({ limit: Number($event.target.value), page: 1 })">
            <option :value="25">25</option><option :value="50">50</option><option :value="100">100</option>
          </select>
        </label>
        <nav :aria-label="t('receiptPagination')">
          <button type="button" :disabled="!page.hasPrevious || loading" :aria-label="t('previousPage')" @click="navigate({ page: page.page - 1 })">‹</button>
          <template v-for="item in pageButtons" :key="item">
            <span v-if="typeof item === 'string'" class="pagination-ellipsis">…</span>
            <button v-else type="button" :class="{ active: item === page.page }" :aria-current="item === page.page ? 'page' : undefined" :disabled="loading" @click="navigate({ page: item })">{{ item }}</button>
          </template>
          <button type="button" :disabled="!page.hasNext || loading" :aria-label="t('nextPage')" @click="navigate({ page: page.page + 1 })">›</button>
        </nav>
      </footer>
    </section>

    <ReceiptDrawer v-if="selectedId != null" :receipt="detail" :loading="detailLoading" :error="detailError" @close="closeDrawer" />
  </section>
</template>
